import React, { useState, useEffect } from 'react';
import './TakeInventoryModal.css';
import InfoIcon from '../../assets/info.svg';

function TakeInventoryModal({ isOpen, onClose, onSave, editingSnapshot = null }) {
  const [formData, setFormData] = useState({
    snapshot_date: new Date().toISOString().split('T')[0],
    team_infinity_points: '',
    personal_infinity_points: '',
    team_monthly_points: '',
    personal_monthly_points: '',
    community_size: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previousValues, setPreviousValues] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (isOpen) {
      if (editingSnapshot) {
        setFormData({
          snapshot_date: editingSnapshot.snapshot_date,
          team_infinity_points: editingSnapshot.team_infinity_points.toString(),
          personal_infinity_points: editingSnapshot.personal_infinity_points.toString(),
          team_monthly_points: editingSnapshot.team_monthly_points.toString(),
          personal_monthly_points: editingSnapshot.personal_monthly_points.toString(),
          community_size: editingSnapshot.community_size.toString()
        });
        setError('');
      } else {
        setFormData({
          snapshot_date: new Date().toISOString().split('T')[0],
          team_infinity_points: '',
          personal_infinity_points: '',
          team_monthly_points: '',
          personal_monthly_points: '',
          community_size: ''
        });
        setError('');
        fetchPreviousValues();
      }
    }
  }, [isOpen, editingSnapshot]);

  const fetchPreviousValues = async () => {
    try {
      const response = await fetch('/backend/api/inventory/get-stats.php', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.history && data.history.length > 0) {
          setPreviousValues(data.history[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching previous values:', error);
    }
  };

  // Format number for display with commas
  const formatNumber = (value) => {
    if (!value) return '';
    const parts = value.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  // Parse formatted number back to raw number
  const parseNumber = (value) => {
    if (!value) return '';
    // Remove all commas, keep periods for decimals
    const cleaned = value.replace(/,/g, '');
    // Allow only numbers and one decimal point
    if (cleaned === '' || /^\d*\.?\d*$/.test(cleaned)) {
      return cleaned;
    }
    return value; // Return original if invalid
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // For numeric fields, clean the input
    if (['team_infinity_points', 'personal_infinity_points', 
         'team_monthly_points', 'personal_monthly_points', 
         'community_size'].includes(name)) {
      const cleaned = parseNumber(value);
      setFormData(prev => ({ ...prev, [name]: cleaned }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleInfoHover = (e, text) => {
    setShowTooltip(true);
    setTooltipData(text);
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  const handleInfoMove = (e) => {
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  const handleInfoLeave = () => {
    setShowTooltip(false);
    setTooltipData(null);
  };

  const validateForm = () => {
    if (!formData.snapshot_date) {
      setError('Date is required');
      return false;
    }

    if (new Date(formData.snapshot_date) > new Date()) {
      setError('Cannot create inventory for future dates');
      return false;
    }

    const fields = ['team_infinity_points', 'personal_infinity_points', 'team_monthly_points', 'personal_monthly_points', 'community_size'];
    for (const field of fields) {
      const value = parseInt(formData[field]);
      if (isNaN(value) || value < 0) {
        setError(`${field.replace('_', ' ')} must be a valid non-negative number`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/backend/api/inventory/save-snapshot.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          snapshot_date: formData.snapshot_date,
          team_infinity_points: parseNumber(formData.team_infinity_points),
          personal_infinity_points: parseNumber(formData.personal_infinity_points),
          team_monthly_points: parseNumber(formData.team_monthly_points),
          personal_monthly_points: parseNumber(formData.personal_monthly_points),
          community_size: parseNumber(formData.community_size)
        })
      });

      const data = await response.json();

      if (data.success) {
        onSave(data);
        onClose();
      } else {
        setError(data.error || 'Failed to save inventory');
      }
    } catch (error) {
      console.error('Error saving inventory:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isHistorical = () => {
    const selectedDate = new Date(formData.snapshot_date);
    const today = new Date();
    const diffTime = today - selectedDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 7;
  };

  const isOldEntry = () => {
    const selectedDate = new Date(formData.snapshot_date);
    const today = new Date();
    const diffTime = today - selectedDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 90;
  };

  if (!isOpen) return null;

  return (
    <div className="take-inventory-overlay" onClick={onClose}>
      <div className="take-inventory-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {editingSnapshot ? 'Edit Inventory Entry' : 
             isHistorical() ? 'Add Historical Inventory' : 'Take This Week\'s Inventory'}
          </h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="inventory-form">
          <div className="form-group">
            <label htmlFor="snapshot_date">Date</label>
            <input
              type="date"
              id="snapshot_date"
              name="snapshot_date"
              value={formData.snapshot_date}
              onChange={handleInputChange}
              max={new Date().toISOString().split('T')[0]}
              required
            />
            {isOldEntry() && (
              <div className="warning-message">
                ⚠️ This is a very old entry. Are you sure?
              </div>
            )}
          </div>

          {previousValues && !editingSnapshot && (
            <div className="previous-values">
              <h4>Previous Week's Values (for reference)</h4>
              <div className="previous-grid">
                <div className="prev-item">
                  <span className="prev-label">Infinity Points:</span>
                  <span className="prev-value">{previousValues.infinity_total.toLocaleString()}</span>
                </div>
                <div className="prev-item">
                  <span className="prev-label">Monthly Points:</span>
                  <span className="prev-value">{previousValues.monthly_total.toLocaleString()}</span>
                </div>
                <div className="prev-item">
                  <span className="prev-label">Community Size:</span>
                  <span className="prev-value">{previousValues.community_size.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          <div className="form-group">
            <div className="input-with-info">
              <label htmlFor="team_infinity_points">Team Infinity Career Points</label>
              <div 
                className="info-icon-wrapper"
                onMouseEnter={(e) => handleInfoHover(e, "Add here your team and your own Infinity Career points.")}
                onMouseMove={handleInfoMove}
                onMouseLeave={handleInfoLeave}
              >
                <img src={InfoIcon} alt="Info" className="info-icon" />
              </div>
            </div>
            <input
              type="text"
              id="team_infinity_points"
              name="team_infinity_points"
              value={formatNumber(formData.team_infinity_points)}
              onChange={handleInputChange}
              placeholder="0"
              required
            />
          </div>

          <div className="form-group">
            <div className="input-with-info">
              <label htmlFor="personal_infinity_points">Personal Infinity Career Points</label>
              <div 
                className="info-icon-wrapper"
                onMouseEnter={(e) => handleInfoHover(e, "Add here your team and your own Infinity Career points.")}
                onMouseMove={handleInfoMove}
                onMouseLeave={handleInfoLeave}
              >
                <img src={InfoIcon} alt="Info" className="info-icon" />
              </div>
            </div>
            <input
              type="text"
              id="personal_infinity_points"
              name="personal_infinity_points"
              value={formatNumber(formData.personal_infinity_points)}
              onChange={handleInputChange}
              placeholder="0"
              required
            />
          </div>

          <div className="form-group">
            <div className="input-with-info">
              <label htmlFor="team_monthly_points">Team Monthly Career Points</label>
              <div 
                className="info-icon-wrapper"
                onMouseEnter={(e) => handleInfoHover(e, "Add here your team and your own Monthly Career points.")}
                onMouseMove={handleInfoMove}
                onMouseLeave={handleInfoLeave}
              >
                <img src={InfoIcon} alt="Info" className="info-icon" />
              </div>
            </div>
            <input
              type="text"
              id="team_monthly_points"
              name="team_monthly_points"
              value={formatNumber(formData.team_monthly_points)}
              onChange={handleInputChange}
              placeholder="0"
              required
            />
          </div>

          <div className="form-group">
            <div className="input-with-info">
              <label htmlFor="personal_monthly_points">Personal Monthly Career Points</label>
              <div 
                className="info-icon-wrapper"
                onMouseEnter={(e) => handleInfoHover(e, "Add here your team and your own Monthly Career points.")}
                onMouseMove={handleInfoMove}
                onMouseLeave={handleInfoLeave}
              >
                <img src={InfoIcon} alt="Info" className="info-icon" />
              </div>
            </div>
            <input
              type="text"
              id="personal_monthly_points"
              name="personal_monthly_points"
              value={formatNumber(formData.personal_monthly_points)}
              onChange={handleInputChange}
              placeholder="0"
              required
            />
          </div>

          <div className="form-group">
            <div className="input-with-info">
              <label htmlFor="community_size">Community Size</label>
              <div 
                className="info-icon-wrapper"
                onMouseEnter={(e) => handleInfoHover(e, "Add here your community size that you see in your Comity backoffice, under the Team.")}
                onMouseMove={handleInfoMove}
                onMouseLeave={handleInfoLeave}
              >
                <img src={InfoIcon} alt="Info" className="info-icon" />
              </div>
            </div>
            <input
              type="text"
              id="community_size"
              name="community_size"
              value={formatNumber(formData.community_size)}
              onChange={handleInputChange}
              placeholder="0"
              required
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Saving...' : (editingSnapshot ? 'Update' : 'Save')}
            </button>
          </div>
        </form>

        {/* Info Tooltip */}
        {showTooltip && tooltipData && (
          <div 
            className="info-tooltip"
            style={{
              left: tooltipPos.x,
              top: tooltipPos.y
            }}
          >
            {tooltipData}
          </div>
        )}
      </div>
    </div>
  );
}

export default TakeInventoryModal;
